import { DEFAULT_MAX_ITEMS } from '../constants';
import { base64Decode, base64Encode } from '../utils/jsonBase64Utils';
import Rollbar from '../utils/rollbar';
import {
  IPostAdsResponse,
  IPostBannerAdsResponse,
  PromiseId,
} from './initWebViewBridge.types';

/**
 * Modified from: https://github.com/appier/appier-web-sdk/blob/web-sdk-spin-off/web-sdk/src/AppProxySDK/index.js
 */
export default function initWebViewBridge() {
  if (!window.rmnMobileSdk) {
    Rollbar.warning('[initWebViewBridge] rmnMobileSdk is not available');
    return;
  }

  const self = window.rmnMobileSdk;
  self._promiseResolverStore = {};

  self._newPromise = function <T>() {
    const promiseId = `${Date.now().toString()}-${Math.random().toString(36).slice(2, 11)}`;
    const promise = new Promise<T>((resolve, reject) => {
      Reflect.set(self._promiseResolverStore, promiseId, { resolve, reject });
    });
    return { id: promiseId, promise };
  };

  self._takePromiseResolver = function (promiseId: PromiseId) {
    const promiseResolver = Reflect.get(self._promiseResolverStore, promiseId);
    Reflect.deleteProperty(self._promiseResolverStore, promiseId);
    return promiseResolver;
  };

  self.callMobileSdkSearchSponsoredProducts = function (params) {
    const {
      search,
      placementId,
      maxItems = DEFAULT_MAX_ITEMS,
      products,
    } = params;
    const { id, promise } = self._newPromise<IPostAdsResponse>();
    self.searchSponsoredProducts(
      id,
      search,
      placementId,
      maxItems,
      base64Encode(JSON.stringify(products)),
    );
    return promise;
  };

  self.resolveSearchSponsoredProducts = function (
    promiseId,
    base64PostAdsResponseString,
  ) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] resolveSearchSponsoredProducts is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.resolve(
      JSON.parse(base64Decode(base64PostAdsResponseString)),
    );
  };

  self.rejectSearchSponsoredProducts = function (
    promiseId,
    errorCode,
    base64ErrorMessage,
  ) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] rejectSearchSponsoredProducts is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.reject({
      errorCode,
      errorMessage: base64Decode(base64ErrorMessage),
    });
  };

  self.callMobileSdkGetBanners = function (params) {
    const { placementId, maxItems } = params;
    const { id, promise } = self._newPromise<IPostBannerAdsResponse>();
    self.getBanners(id, placementId, maxItems ?? 0);
    return promise;
  };

  self.resolveGetBanners = function (
    promiseId,
    base64PostBannerAdsResponseString,
  ) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] resolveGetBanners is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.resolve(
      JSON.parse(base64Decode(base64PostBannerAdsResponseString)),
    );
  };

  self.rejectGetBanners = function (promiseId, errorCode, base64ErrorMessage) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] rejectGetBanners is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.reject({
      errorCode,
      errorMessage: base64Decode(base64ErrorMessage),
    });
  };

  self.callMobileSdkRecommendSponsoredProducts = function (params) {
    const {
      category,
      placementId,
      maxItems = DEFAULT_MAX_ITEMS,
      products,
    } = params;
    const { id, promise } = self._newPromise<IPostAdsResponse>();
    self.recommendSponsoredProducts(
      id,
      category,
      placementId,
      maxItems,
      base64Encode(JSON.stringify(products)),
    );
    return promise;
  };

  self.resolveRecommendSponsoredProducts = function (
    promiseId,
    base64PostAdsResponseString,
  ) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] resolveRecommendSponsoredProducts is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.resolve(
      JSON.parse(base64Decode(base64PostAdsResponseString)),
    );
  };

  self.rejectRecommendSponsoredProducts = function (
    promiseId,
    errorCode,
    base64ErrorMessage,
  ) {
    const promiseResolver = self._takePromiseResolver(promiseId);
    if (!promiseResolver) {
      Rollbar.warning(
        `[initWebViewBridge] rejectRecommendSponsoredProducts is called with an unknown promiseId: ${promiseId}`,
      );
      return;
    }
    promiseResolver.reject({
      errorCode,
      errorMessage: base64Decode(base64ErrorMessage),
    });
  };
}
